import axios from 'axios';
import { getUserFriendlyError } from '../utils/errorMessages';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true, // Important for cookies
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add request interceptor to include session token from localStorage
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('session_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
}, (error) => {
  return Promise.reject(error);
});

// Add response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Don't auto-redirect on 401 - let AuthContext handle authentication state
    // This prevents infinite loops with localStorage fallback mechanism
    if (error.response?.status === 401) {
      // Just log the error, don't redirect
      console.log('API 401 error - auth state will be handled by AuthContext');
    }
    
    // Enhance error with user-friendly message
    if (error.response) {
      error.userMessage = getUserFriendlyError(error);
    } else if (error.message === 'Network Error' || error.code === 'ERR_NETWORK') {
      error.userMessage = 'Unable to connect. Please check your internet connection and try again.';
    } else {
      error.userMessage = getUserFriendlyError(error);
    }
    
    return Promise.reject(error);
  }
);

// Authentication APIs
export const authAPI = {
  exchangeSession: (sessionId) => 
    api.post('/auth/exchange-session', { session_id: sessionId }),
  
  getMe: () => 
    api.get('/auth/me'),
  
  logout: () => 
    api.post('/auth/logout'),
  
  updateProfile: (preferences) => 
    api.put('/auth/profile', { preferences })
};

// Chat APIs
export const chatAPI = {
  getMessages: (sessionId = 'default') => 
    api.get(`/chat/messages?session_id=${sessionId}`),
  
  sendMessage: (message, sessionId = 'default', language = 'en') => 
    api.post('/chat/message', { message, session_id: sessionId, language })
};

// Work APIs
export const workAPI = {
  getTasks: () => 
    api.get('/work/tasks'),
  
  createTask: (taskData) => 
    api.post('/work/tasks', taskData),
  
  updateTask: (taskId, taskData) => 
    api.put(`/work/tasks/${taskId}`, taskData),
  
  deleteTask: (taskId) => 
    api.delete(`/work/tasks/${taskId}`),
  
  getDocuments: () =>
    api.get('/work/documents'),
  
  saveDocument: (docData) =>
    api.post('/work/documents', docData),
  
  deleteDocument: (docId) =>
    api.delete(`/work/documents/${docId}`),
  
  updateDocument: (docId, docData) =>
    api.put(`/work/documents/${docId}`, docData)
};

// Health APIs
export const healthAPI = {
  getTodayData: () => 
    api.get('/health/today'),
  
  logSteps: (steps) => 
    api.post('/health/steps', { steps }),
  
  getMedicines: () => 
    api.get('/health/medicines'),
  
  addMedicine: (medicineData) => 
    api.post('/health/medicines', medicineData),
  
  deleteMedicine: (medicineId) =>
    api.delete(`/health/medicines/${medicineId}`),
  
  getWorkouts: () =>
    api.get('/health/workouts'),
  
  logWorkout: (workoutData) =>
    api.post('/health/workouts', workoutData),
  
  logSymptoms: (symptomData) =>
    api.post('/health/symptoms', symptomData),
  
  markMedicineTaken: (medicineId) =>
    api.post(`/health/medicine-taken/${medicineId}`),
  
  getFoods: () =>
    api.get('/health/foods'),
  
  logFood: (foodData) =>
    api.post('/health/foods', foodData),
  
  deleteFood: (foodId) =>
    api.delete(`/health/foods/${foodId}`),
  
  updateFood: (foodId, foodData) =>
    api.put(`/health/foods/${foodId}`, foodData),
  
  medicalAssistant: (message) =>
    api.post('/health/medical-assistant', { message }),
  
  logWater: (glasses = 1) =>
    api.post('/health/water', null, { params: { glasses } }),
  
  getWaterReminders: () =>
    api.get('/health/water-reminders'),
  
  updateWaterReminders: (settings) =>
    api.put('/health/water-reminders', settings),
  
  getWaterStatus: () =>
    api.get('/health/water-status'),
  
  getHealthHistory: (date) =>
    api.get(`/health/history/${date}`),
  
  getMonthlySummary: (year, month) =>
    api.get(`/health/monthly-summary/${year}/${month}`)
};

// Life APIs
export const lifeAPI = {
  getBills: () =>
    api.get('/life/bills'),
  
  createBill: (billData) =>
    api.post('/life/bills', billData),
  
  updateBill: (billId, billData) =>
    api.put(`/life/bills/${billId}`, billData),
  
  deleteBill: (billId) =>
    api.delete(`/life/bills/${billId}`),
  
  getAppointments: () =>
    api.get('/life/appointments'),
  
  createAppointment: (appointmentData) =>
    api.post('/life/appointments', appointmentData),
  
  deleteAppointment: (appointmentId) =>
    api.delete(`/life/appointments/${appointmentId}`),
  
  getExpenses: () =>
    api.get('/life/expenses'),
  
  createExpense: (expenseData) =>
    api.post('/life/expenses', expenseData),
  
  deleteExpense: (expenseId) =>
    api.delete(`/life/expenses/${expenseId}`),
  
  getMonthlyExpenses: () =>
    api.get('/life/monthly-expenses'),
  
  getExpensesByDate: (date) =>
    api.get(`/life/expenses/date/${date}`),
  
  getMonthlyCalendar: (year, month) =>
    api.get(`/life/monthly-calendar/${year}/${month}`)
};

// Google Calendar API
export const calendarAPI = {
  // Google Calendar
  initiateGoogleLogin: (userId) =>
    api.get('/auth/google/calendar-login', { params: { user_id: userId } }),
  
  syncCalendar: () =>
    api.post('/calendar/sync'),
  
  getCalendarStatus: () =>
    api.get('/calendar/status'),
  
  disconnectCalendar: () =>
    api.post('/calendar/disconnect'),
  
  // Apple Calendar
  connectAppleCalendar: (credentials) =>
    api.post('/calendar/apple/connect', credentials),
  
  syncAppleCalendar: () =>
    api.post('/calendar/apple/sync'),
  
  disconnectAppleCalendar: () =>
    api.post('/calendar/apple/disconnect'),
  
  // All Calendars Status
  getAllCalendarStatus: () =>
    api.get('/calendar/status/all')
};

// User APIs
export const userAPI = {
  getSubscription: () =>
    api.get('/user/subscription')
};

// Payment APIs
export const paymentAPI = {
  createSubscriptionCheckout: (planId, originUrl) =>
    api.post('/payments/checkout/subscription', { plan_id: planId, origin_url: originUrl }),
  
  createCreditsCheckout: (packId, originUrl) =>
    api.post('/payments/checkout/credits', { pack_id: packId, origin_url: originUrl }),
  
  getPaymentStatus: (sessionId) =>
    api.get(`/payments/status/${sessionId}`),
  
  getPlans: () =>
    api.get('/payments/plans')
};

// Home Dashboard API
export const homeAPI = {
  getOverview: () => 
    api.get('/home/overview')
};

export default api;
